import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addMonths, format, startOfMonth } from 'date-fns';
import { uk } from 'date-fns/locale';

import { LayoutContext } from '../../contexts/layout.context';

import { authSelectors } from '../../store/selectors/auth';
import { profileSelectors } from '../../store/selectors/profile';
import { scheduleSelectors } from '../../store/selectors/schedule';
import { getDay, getMonth } from '../../store/thunks/schedule';
import { getUserInfoThunk } from '../../store/thunks/profile';

import { getToken } from '../../utils/token';
import { toDateKey } from '../../components/schedule/constants';
import { forgetHomeworkEvent, readHomeworkEvent } from '../../components/schedule/homework-handoff';

import HeadTooltip from '../../components/Tooltip/HeadTooltip';
import CourseTooltipHint from '../../components/Tooltip/CourseTooltipHint';
import EventModal from '../../components/schedule/event-modal.component';
import ScheduleMonth from '../../components/schedule/schedule-month.component';
import ScheduleLegend from '../../components/schedule/ScheduleLegend/ScheduleLegend';
import ScheduleTimezoneNote from '../../components/schedule/ScheduleTimezoneNote/ScheduleTimezoneNote';
import ScheduleActions from '../../components/ScheduleV2/ScheduleActions';
import ScheduleListV2 from '../../components/ScheduleV2/ScheduleListV2';
import usePreviewFeature from '../../hooks/usePreviewFeature';
import '../../components/ScheduleV2/schedule-v2.scss';

/** Формат, який чекає бекенд і в show_month, і в ?date= (Carbon парсить саме d.m.Y). */
const API_DATE = 'dd.MM.yyyy';

const dateFromKey = (key) => new Date(`${key}T00:00:00`);

const Schedule = () => {
  const dispatch = useDispatch();
  const { setLayout } = useContext(LayoutContext);
  const isV2 = usePreviewFeature('cabinet-v2');

  const auth = useSelector(authSelectors);
  const token = auth.token;
  getToken(token);

  const { month, monthLoading, monthError, day, dayLoading, dayError } = useSelector(scheduleSelectors);

  // Профіль уже вантажиться нижче (getUserInfoThunk) — звідси беремо обраний часовий пояс.
  const user = useSelector(profileSelectors).user;

  // Перехід із ДЗ повертає користувача на дату тієї події. Читаємо один раз при монтуванні.
  const handoffDate = useMemo(() => {
    const stored = readHomeworkEvent();
    return stored?.date ? dateFromKey(toDateKey(stored.date)) : null;
  }, []);

  const [selectedDate, setSelectedDate] = useState(() => toDateKey(handoffDate || new Date()));
  const [activeStartDate, setActiveStartDate] = useState(() => startOfMonth(handoffDate || new Date()));
  const [modalOpen, setModalOpen] = useState('');

  const monthKey = useMemo(() => format(activeStartDate, API_DATE), [activeStartDate]);

  useEffect(() => {
    document.title = 'Розклад | New Brain';
    setLayout({ page: 'Розклад' });
  }, [setLayout]);

  // Дані з ДЗ уже застосовані до стартової дати — далі вони тільки заважали б.
  useEffect(() => {
    forgetHomeworkEvent();
  }, []);

  useEffect(() => {
    if (token) dispatch(getUserInfoThunk({ token }));
  }, [token, dispatch]);

  // Місяць вантажиться тільки при зміні місяця, день — тільки при зміні дня.
  // Раніше обидва ефекти залежали ще й від дати-навпаки, тому на старті летіло 3-4 запити.
  // Перший показ місяця завжди йде в API повз кеш: студент міг щойно виконати ДЗ
  // і повернутись у розклад — колір іконки має бути свіжим.
  const isFirstMonthLoad = useRef(true);

  useEffect(() => {
    if (!token) return;

    dispatch(getMonth({ monthKey, token, force: isFirstMonthLoad.current }));
    isFirstMonthLoad.current = false;
  }, [token, monthKey, dispatch]);

  const reloadMonth = useCallback(() => {
    if (!token) return;
    dispatch(getMonth({ monthKey, token, force: true }));
  }, [token, monthKey, dispatch]);

  const fetchDay = useCallback((dayKey) => {
    if (!token || !dayKey) return;
    dispatch(getDay({ dayKey, dateParam: format(dateFromKey(dayKey), API_DATE), token }));
  }, [token, dispatch]);

  useEffect(() => {
    fetchDay(selectedDate);
  }, [selectedDate, fetchDay]);

  // На вузькому екрані підкручуємо календар до обраного дня — панель відкривається знизу.
  useEffect(() => {
    if (window.innerWidth >= 768) return;

    const chosen = document.querySelector('.choose-day');
    if (chosen) chosen.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [selectedDate]);

  const handleClickDay = (value) => {
    setModalOpen('open');
    setSelectedDate(toDateKey(value));
  };

  const dateLabel = useMemo(
    () => format(dateFromKey(selectedDate), 'EEEE, d MMMM', { locale: uk }),
    [selectedDate]
  );

  const monthLabel = useMemo(
    () => format(activeStartDate, 'LLLL yyyy', { locale: uk }).replace(/^./, (ch) => ch.toUpperCase()),
    [activeStartDate]
  );

  return (
    <div className={`schedule ${isV2 ? 'schedule--v2' : ''}`.trim()}>

      <div className="schedule__tooltip">
        <HeadTooltip text={<CourseTooltipHint />} />
      </div>

      <div className="schedule__events">
        <EventModal
          events={day}
          loading={dayLoading}
          error={dayError}
          dateLabel={dateLabel}
          timezoneCity={user.timezone_city}
          timezoneOffsetLabel={user.timezone_offset_label}
          toggleClass={modalOpen}
          closeModal={() => setModalOpen('')}
          // Повторний запит того самого дня під час активного дав би миготіння порожнього стану.
          onRetry={() => !dayLoading && fetchDay(selectedDate)}
        />
      </div>

      <div className={`schedule-month ${monthLoading ? 'is-loading' : ''}`}>
        <ScheduleTimezoneNote city={user.timezone_city} offsetLabel={user.timezone_offset_label} />

        {monthError && (
          <div className="schedule-month__error">
            {monthError}
            <button type="button" className="schedule-month__retry" onClick={reloadMonth}>
              Спробувати ще
            </button>
          </div>
        )}

        {isV2 && (
          <ScheduleListV2
            events={Array.isArray(month) ? month : []}
            monthLabel={monthLabel}
            selectedDate={selectedDate}
            onPickDay={handleClickDay}
            onPrev={() => setActiveStartDate(startOfMonth(addMonths(activeStartDate, -1)))}
            onNext={() => setActiveStartDate(startOfMonth(addMonths(activeStartDate, 1)))}
          />
        )}

        <div className={isV2 ? 'sv2-calendar' : ''}>
          <ScheduleMonth
            dates={month}
            selectedDate={selectedDate}
            handleClick={handleClickDay}
            activeStartDate={activeStartDate}
            setActiveStartDate={setActiveStartDate}
            markAttendance={isV2}
          />
        </div>
        {isV2 && <ScheduleActions token={token} />}
        <ScheduleLegend />
      </div>
    </div>
  );
};

export default Schedule;
